import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTabDrafts } from '@/stores/tabDrafts'
import { useShellHref } from '@/utils/router'
import { cn } from '@/lib/utils'
import type { WorkspaceTab } from '@/stores/app'
import type { CloseOutcome } from '@/stores/closeOwners'

import { useTabOverflow } from './useTabOverflow'
import { TabBarOverflow } from './TabBarOverflow'
import { getTabIcon } from './tabIcon'
import { getTabTooltip } from './tabTooltip'

interface TabBarProps {
  tabs: WorkspaceTab[]
  activeId: string | null
  /** Resolves with what actually happened — a dirty tab may prompt first. */
  onCloseTab: (id: string) => Promise<CloseOutcome>
  /** Overflow dropdown only — the visible row navigates through its links. */
  onSelectTab: (id: string) => void
}

// Row geometry, lifted off the old <TabsList>.
const TAB_ROW =
  'flex w-full min-w-0 h-fit items-center justify-start gap-0 bg-transparent'

// Pill styling, once the `pill` variant of <TabsTrigger> and now owned here —
// the shelf was its only consumer. It sits on the wrapper, not the link, so the
// close button stays inside the pill.
const TAB_PILL = cn(
  'group relative inline-flex items-center gap-2 h-10 min-w-[52px] px-4 rounded-t-lg',
  'text-sm whitespace-nowrap transition-[color,background-color,border-color,box-shadow,opacity]',
  'data-[state=inactive]:text-black-alpha-11',
  'hover:bg-tab data-[state=active]:bg-tab',
  'hover:text-primary-high-contrast data-[state=active]:text-primary-high-contrast',
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[18px]"
)

/**
 * Tab strip with render-and-measure overflow.
 *
 * Renders two rows inside the same container:
 *   - A hidden measurement row (aria-hidden, pointer-events:none) of plain
 *     spans with icon + title only. Used to measure each tab's natural width.
 *     It carries no dirty marker and no close button, so dirty toggles never
 *     churn the measurement, and no `data-slot=tabs-trigger` — one node per
 *     tab keeps the CSS and e2e selectors unambiguous.
 *   - The visible row: a list of one link per tab plus a sibling close button,
 *     dirty markers, and a trailing "+N" dropdown for overflow.
 *
 * Each tab is a real link. Plain click activates the tab, cmd/ctrl-click opens
 * its URL in a new BROWSER tab (never intercepted), middle-click closes the
 * workspace tab. The whole pill is the click target: the link stretches an
 * `after:inset-0` overlay across the pill, and the label and close button ride
 * above it on `relative z-10` so they keep their own hover and click.
 *
 * Keyboard: the whole shelf is ONE tab stop (roving tabindex). ArrowLeft /
 * ArrowRight move focus between tabs without activating them, Home / End jump
 * to the ends, Enter follows the focused link, Delete or Backspace closes the
 * focused tab. The close buttons stay out of the tab order — pointer and AT
 * click only.
 */
export function TabBar({
  tabs,
  activeId,
  onCloseTab,
  onSelectTab,
}: TabBarProps) {
  const containerRef = useRef<HTMLElement | null>(null)
  const linkRefs = useRef(new Map<string, HTMLAnchorElement>())
  const shellHref = useShellHref()
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const { visibleTabs, overflowTabs, getMeasureRef } = useTabOverflow({
    tabs,
    activeId,
    containerRef,
  })

  // Roving tabindex: the shelf keeps exactly one reachable link. It is the one
  // the user last focused; when that tab is gone (closed, or pushed into the
  // overflow dropdown) the active tab takes the tab stop back.
  const isVisible = (id: string | null) => visibleTabs.some((t) => t.id === id)
  const rovingId = isVisible(focusedId)
    ? focusedId
    : isVisible(activeId)
      ? activeId
      : (visibleTabs[0]?.id ?? null)

  const handleClose = async (tab: WorkspaceTab) => {
    // Resolve the focus target from the PRE-close DOM: left neighbour first —
    // the same clamp the store uses to pick the next active tab, so focus
    // lands on the tab that takes over. A neighbour sitting in the overflow
    // dropdown has no link — fall back to the nav.
    const index = tabs.findIndex((t) => t.id === tab.id)
    const neighbor = tabs[index - 1] ?? tabs[index + 1]
    const target = neighbor ? linkRefs.current.get(neighbor.id) : undefined
    const nav = containerRef.current
    // A dirty tab prompts first, so only that path is async. Split them:
    const willPrompt = useTabDrafts.getState().isDirty(tab.id)
    if (!willPrompt) {
      // Move focus while the pre-close DOM is still up: React only flushes the
      // close after this handler returns, and it keeps the neighbour's link
      // node (keyed by tab id) across the re-render, so focus survives.
      ;(target ?? nav)?.focus()
    }
    const outcome = await onCloseTab(tab.id)
    // Move focus only once the tab really closed — otherwise a cancelled close
    // would rip focus off the tab the user chose to keep.
    if (!willPrompt || outcome !== 'closed') return
    // The shelf re-renders freely while the dialog is up, so the captured link
    // node may be detached by the time the user decides.
    const focusTarget = target?.isConnected ? target : nav
    focusTarget?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    // Tab links only — the "+N" dropdown trigger lives in the same row and
    // owns its own keys.
    if ((event.target as HTMLElement).dataset.slot !== 'tabs-trigger') return
    const from = visibleTabs.findIndex((t) => t.id === rovingId)
    if (from === -1) return
    const focusAt = (index: number) => {
      const clamped = Math.min(Math.max(index, 0), visibleTabs.length - 1)
      const id = visibleTabs[clamped]?.id
      if (id) linkRefs.current.get(id)?.focus()
    }
    switch (event.key) {
      case 'ArrowLeft':
        focusAt(from - 1)
        break
      case 'ArrowRight':
        focusAt(from + 1)
        break
      case 'Home':
        focusAt(0)
        break
      case 'End':
        focusAt(visibleTabs.length - 1)
        break
      case 'Delete':
      case 'Backspace':
        void handleClose(visibleTabs[from]!)
        break
      default:
        return
    }
    // Only reached by a handled key: no page scroll, no browser Back.
    event.preventDefault()
  }

  return (
    <nav
      ref={containerRef}
      aria-label="Open work"
      // Focus target of last resort after a close. -1 keeps it out of the tab
      // order; the ring is suppressed because only script focuses it.
      tabIndex={-1}
      className="peer/tabbar relative shrink-0 overflow-x-hidden outline-none">
      {/* The `tabs-list` / `tabs-trigger` / `tabs-content` slot names outlive
          Radix deliberately: stylesheet selectors and the Playwright specs key
          off them, so renaming one means renaming all three sets together. */}
      <div
        aria-hidden="true"
        data-slot="tabs-list"
        className={cn(
          TAB_ROW,
          'pointer-events-none invisible absolute inset-0'
        )}>
        {tabs.map((tab) => {
          const Icon = getTabIcon(tab.routeId)
          return (
            <span key={tab.id} ref={getMeasureRef(tab.id)} className={TAB_PILL}>
              {Icon && <Icon size={16} className="shrink-0" />}
              <span>{tab.title}</span>
            </span>
          )
        })}
      </div>

      {/* Visible row. `role=list` is explicit: the flex display strips list
          semantics in Safari/VoiceOver. TooltipProvider is context-only
          (renders no DOM), so the pills stay direct children of the row. */}
      <ul
        role="list"
        data-slot="tabs-list"
        onKeyDown={handleKeyDown}
        className={TAB_ROW}>
        <TooltipProvider delayDuration={300}>
          {visibleTabs.map((tab) => {
            const Icon = getTabIcon(tab.routeId)
            const tooltip = getTabTooltip(tab.routeId)
            const isActive = tab.id === activeId
            const state = isActive ? 'active' : 'inactive'
            // The tooltip wraps this inner label, never the link itself. z-10
            // keeps the label above the link's stretched overlay, otherwise the
            // overlay swallows the hover that opens the tooltip.
            const label = (
              <span className="relative z-10 inline-flex items-center gap-2 min-w-0">
                {Icon && <Icon size={16} className="shrink-0" />}
                <DirtyMarker tabId={tab.id} />
                <span className="truncate">{tab.title}</span>
              </span>
            )
            return (
              <li key={tab.id} data-state={state} className={TAB_PILL}>
                <Link
                  to={shellHref(tab.href)}
                  ref={(el) => {
                    if (el) linkRefs.current.set(tab.id, el)
                    return () => {
                      linkRefs.current.delete(tab.id)
                    }
                  }}
                  data-slot="tabs-trigger"
                  data-state={state}
                  aria-current={isActive ? 'page' : undefined}
                  tabIndex={tab.id === rovingId ? 0 : -1}
                  onFocus={() => setFocusedId(tab.id)}
                  // The active tab's href resolves to the URL we are already
                  // on, byte for byte — mergeShellSearch keeps both sides'
                  // original encoding — so replace keeps that click out of the
                  // history stack instead of stacking a duplicate entry. The
                  // one exception is an open mobile drawer: mobilePanel is
                  // ephemeral and never rides along, so clicking the active tab
                  // closes the drawer and replaces the entry.
                  replace={isActive}
                  // Middle-click closes the tab, the browser-tab convention.
                  // preventDefault on mousedown kills autoscroll; on auxclick it
                  // kills the open-in-new-browser-tab default.
                  onMouseDown={(e) => {
                    if (e.button === 1) e.preventDefault()
                  }}
                  onAuxClick={(e) => {
                    if (e.button !== 1) return
                    e.preventDefault()
                    void handleClose(tab)
                  }}
                  // `after:inset-0` stretches the link over the whole pill (the
                  // <li> is the positioned ancestor), so any dead space in the
                  // pill activates the tab.
                  className="inline-flex items-center truncate max-w-full min-w-[10px] rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary-solid focus-visible:ring-offset-2 after:absolute after:inset-0 after:rounded-t-lg after:content-['']">
                  {tooltip ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{label}</TooltipTrigger>
                      <TooltipContent>{tooltip}</TooltipContent>
                    </Tooltip>
                  ) : (
                    label
                  )}
                </Link>
                <button
                  type="button"
                  aria-label={`Close ${tab.title}`}
                  onClick={() => void handleClose(tab)}
                  // Out of the tab order: pointer and AT click only. The
                  // keyboard path is Delete/Backspace on the focused tab.
                  tabIndex={-1}
                  // z-10 lifts it above the link's stretched overlay.
                  className="relative z-10 inline-flex size-5 shrink-0 items-center justify-center rounded-full text-current invisible group-hover:visible group-focus-within:visible group-data-[state=active]:visible hover:bg-black/10 outline-none focus-visible:ring-2 focus-visible:ring-primary-solid">
                  <X className="size-3.5" />
                </button>
              </li>
            )
          })}
          {overflowTabs.length > 0 && (
            <li>
              <TabBarOverflow tabs={overflowTabs} onSelect={onSelectTab} />
            </li>
          )}
        </TooltipProvider>
      </ul>
    </nav>
  )
}

function DirtyMarker({ tabId }: { tabId: string }) {
  const isDirty = useTabDrafts((s) => s.isDirty(tabId))
  if (!isDirty) return null
  // role=img is what gives the marker an accessible name — a bare aria-label
  // on a role-less span is dropped by assistive tech.
  return (
    <span role="img" aria-label="Unsaved changes" className="text-blue-500">
      *
    </span>
  )
}
